/**
 * Utility functions for handling images in the existing backend system
 */

// Get the base URL for images based on environment
export const getImageBaseUrl = (): string => {
  // In production, use the configured site URL
  const siteUrl = (import.meta as any).env?.VITE_SITE_URL || 'https://cdn.laboratoireluxecosmetique.com'
  return `${siteUrl}/products`
}

// Generic assets base URL (for videos or other files not under /products)
export const getAssetBaseUrl = (): string => {
  const siteUrl = (import.meta as any).env?.VITE_SITE_URL || 'https://cdn.laboratoireluxecosmetique.com'
  return `${siteUrl}/ads`
}

// Convert a filename or path to a full image URL
export const getImageUrl = (src: string | null | undefined): string | null => {
  if (!src) return null
  
  // If it's already a full URL, return as is
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src
  }
  
  // If it's a blob URL (for previews), return as is
  if (src.startsWith('blob:')) {
    return src
  }
  
  const baseUrl = getImageBaseUrl()
  
  // Remove any leading slashes and path separators
  const cleanSrc = src.replace(/^[\/\\]+/, '').replace(/\\/g, '/')
  
  // If the src is just a filename, use it directly
  if (!cleanSrc.includes('/')) {
    return `${baseUrl}/${cleanSrc}`
  }
  
  // If it contains path info, extract just the filename
  const filename = cleanSrc.split('/').pop() || cleanSrc
  return `${baseUrl}/${filename}`
}

// Extract filename from a path or URL
export const getFilenameFromPath = (path: string): string => {
  if (!path) return ''
  
  // Handle Windows and Unix paths
  const cleanPath = path.replace(/\\/g, '/')
  const parts = cleanPath.split('/')
  return parts[parts.length - 1] || ''
}

// Validate if a file is a supported image format
export const isSupportedImageFormat = (file: File): boolean => {
  const supportedTypes = ['image/webp', 'image/jpeg', 'image/jpg', 'image/png']
  return supportedTypes.includes(file.type)
}

// Validate file size (in MB)
export const isValidFileSize = (file: File, maxSizeMB: number = 2): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  return file.size <= maxSizeBytes
}

// Convert file size to human readable format
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Check if WebP is supported by the browser
export const isWebPSupported = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const webP = new Image()
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2)
    }
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA'
  })
}

// Optimize image for upload (convert to WebP if supported and beneficial)
export const optimizeImageForUpload = async (file: File): Promise<File> => {
  // For now, return the original file
  // In a more advanced implementation, we could:
  // 1. Check if WebP is supported
  // 2. Convert JPEG/PNG to WebP if it would reduce file size
  // 3. Resize images if they're too large
  return file
}

// Convert a filename or path to a full asset URL (for videos or other files)
export const getAssetUrl = (src: string | null | undefined): string => {
  if (!src) return ''

  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src
  }

  if (src.startsWith('blob:')) {
    return src
  }

  const baseUrl = getAssetBaseUrl()
  const cleanSrc = src.replace(/^[\/\\]+/, '').replace(/\\/g, '/')
  const filename = cleanSrc.split('/').pop() || cleanSrc
  return `${baseUrl}/${filename}`
}